import type { ComponentProps } from 'react';
import { FeatureGate } from '@frameio/plan-management/feature-gate/components';
import { useFeatureGatePlanSuggestion } from '@frameio/plan-management/feature-gate/hooks';
import { useFeatureGateDisableContext } from '@frameio/plan-management/feature-gate/hooks/useFeatureGateDisableContext';
import { RestrictedAccessToggle } from '@frameio/shared/components';
import { RESTRICTED_PROJECTS_FEATURE_INPUT } from '../../constants';
import TEXT from './generated/TEXT/ProjectAccessSwitch';

type ProjectAccessToggleProps = {
  canManageAccess?: boolean;
  isRestricted: boolean;
  onChange: () => void;
  showIsRestrictedDescription?: boolean;
} & Pick<ComponentProps<typeof RestrictedAccessToggle>, 'isLastToggle'>;
TEXT.DEFINE({
  /** Toggle title for making a project restricted */
  title: 'Make Restricted',
  /** Description for unrestricted project access */
  description: 'Only people directly invited to the project will have access.',
  /** Description when the project is already restricted */
  restrictedDescription:
    'Only people directly invited to the project can access.',
});
export const ProjectAccessSwitch = ({
  canManageAccess = true,
  isRestricted,
  isLastToggle,
  onChange,
  showIsRestrictedDescription = false,
}: ProjectAccessToggleProps) => {
  // Bypass FeatureGate wrapper for restricted projects
  // This ensures users can always unrestrict their projects regardless of plan
  return isRestricted ? (
    <ToggleContainer
      canManageAccess={canManageAccess}
      isLastToggle={isLastToggle}
      isRestricted={isRestricted}
      onChange={onChange}
      showIsRestrictedDescription={showIsRestrictedDescription}
    />
  ) : (
    <FeatureGate
      feature={RESTRICTED_PROJECTS_FEATURE_INPUT}
      placement="top"
      shouldShowTooltip={true}
    >
      <ToggleContainer
        canManageAccess={canManageAccess}
        isLastToggle={isLastToggle}
        isRestricted={isRestricted}
        onChange={onChange}
        showIsRestrictedDescription={showIsRestrictedDescription}
      />
    </FeatureGate>
  );
};
/**
 * Handles feature gate logic with special consideration for restricted projects:
 * - When a project is already restricted, feature gate checks are bypassed to allow unrestricting
 * - When a project is not restricted, feature gate determines if the user can restrict based on their plan
 */
const ToggleContainer = ({
  canManageAccess = true,
  isLastToggle,
  isRestricted,
  onChange,
  showIsRestrictedDescription,
}: ProjectAccessToggleProps) => {
  const featureGatePlanSuggestion = useFeatureGatePlanSuggestion(
    RESTRICTED_PROJECTS_FEATURE_INPUT
  );
  const shouldUseFeatureGateButton =
    !featureGatePlanSuggestion?.includedInCurrentPlan;
  const featureGateDisableContext = useFeatureGateDisableContext();
  const isFeatureGateActive =
    featureGateDisableContext?.isUpgradeModalButtonActive ?? false;
  return (
    <RestrictedAccessToggle
      canManageAccess={canManageAccess}
      description={
        showIsRestrictedDescription && isRestricted
          ? TEXT.restrictedDescription
          : TEXT.description
      }
      // If project is already restricted, bypass feature gate checks to allow unrestricting
      isFeatureGateActive={isRestricted ? false : isFeatureGateActive}
      // If project is already restricted, bypass feature gate checks
      isFeatureGated={isRestricted ? false : shouldUseFeatureGateButton}
      isLastToggle={isLastToggle}
      isRestricted={isRestricted}
      onChange={onChange}
      title={TEXT.title}
    />
  );
};
